import { Controller } from '@hotwired/stimulus';

const PALETTE = [
  'rgba(16, 185, 129, 0.2)',
  'rgba(52, 211, 153, 0.16)',
  'rgba(167, 243, 208, 0.38)',
  'rgba(209, 250, 229, 0.45)',
  'rgba(45, 212, 191, 0.15)',
  'rgba(56, 189, 248, 0.14)',
  'rgba(167, 139, 250, 0.12)',
];

function easeInCubic(t) {
  return t * t * t;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default class extends Controller {
  static values = {
    cols: { type: Number, default: 26 },
    rows: { type: Number, default: 15 },
    forwardMs: { type: Number, default: 15000 },
    reverseMs: { type: Number, default: 11000 },
  };

  connect() {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      this.element.replaceChildren();
      return;
    }

    this._startGrid = () => this.startGrid();
    if ('requestIdleCallback' in window) {
      this.idleId = requestIdleCallback(this._startGrid, { timeout: 2000 });
    } else {
      this.timeoutId = setTimeout(this._startGrid, 0);
    }
  }

  disconnect() {
    if (this.idleId) {
      cancelIdleCallback(this.idleId);
      this.idleId = null;
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    // Drop the grid state too, so a later connect cannot animate against cells
    // from a teardown that already happened.
    this.cellElements = null;
    this.shuffleOrder = null;
  }

  startGrid() {
    // Never leave a previous frame loop running. startGrid used to overwrite
    // this.rafId without cancelling, so a second run (Turbo restore, a repeat
    // connect) orphaned the old loop for a frame. That loop kept its own cell
    // count in scope but read shuffleOrder/cellElements off the instance, which
    // buildGrid had just replaced -- so it indexed past the new, possibly
    // smaller arrays and threw on undefined.classList.
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.cols = this.pickCols(this.colsValue);
    this.rows = this.rowsValue;
    this.buildGrid();

    this.shuffleOrder = shuffle([...Array(this.totalCells).keys()]);
    this.prevCount = 0;
    this.startTime = performance.now();

    this._loop = (now) => {
      const cells = this.cellElements;
      const order = this.shuffleOrder;
      if (!cells || !order) return;

      // Read the size from the live arrays rather than capturing it, so the
      // loop can never outlive the grid it is animating.
      const n = Math.min(cells.length, order.length);
      if (n === 0) return;

      const total = this.forwardMsValue + this.reverseMsValue;
      const elapsed = (now - this.startTime) % total;

      let count;
      if (elapsed < this.forwardMsValue) {
        const u = elapsed / this.forwardMsValue;
        count = Math.floor(easeInCubic(u) * n);
      } else {
        const u = (elapsed - this.forwardMsValue) / this.reverseMsValue;
        count = Math.floor((1 - easeInCubic(u)) * n);
      }

      const prev = Math.min(this.prevCount, n);
      if (count !== prev) {
        if (count > prev) {
          for (let k = prev; k < count; k += 1) {
            const cell = cells[order[k]];
            if (cell) cell.classList.add('bs-hero-splash__grid-cell--lit');
          }
        } else {
          for (let k = count; k < prev; k += 1) {
            const cell = cells[order[k]];
            if (cell) cell.classList.remove('bs-hero-splash__grid-cell--lit');
          }
        }
        this.prevCount = count;
      }

      this.rafId = requestAnimationFrame(this._loop);
    };

    this.rafId = requestAnimationFrame(this._loop);
  }

  get totalCells() {
    return this.cols * this.rows;
  }

  pickCols(pref) {
    if (typeof window === 'undefined') return pref;
    return window.matchMedia('(max-width: 639px)').matches ? Math.min(pref, 16) : pref;
  }

  buildGrid() {
    this.element.replaceChildren();

    const cols = this.cols;
    const rows = this.rows;
    this.element.style.setProperty('--grid-cols', String(cols));
    this.element.style.setProperty('--grid-rows', String(rows));

    const frag = document.createDocumentFragment();
    const cellElements = [];
    const n = cols * rows;

    for (let i = 0; i < n; i += 1) {
      const cell = document.createElement('span');
      cell.className = 'bs-hero-splash__grid-cell';
      cell.style.setProperty('--lit-bg', PALETTE[i % PALETTE.length]);
      frag.appendChild(cell);
      cellElements.push(cell);
    }

    this.element.appendChild(frag);
    this.cellElements = cellElements;
  }
}
